'use client';

/**
 * RunbookConfigUpdater - Client component that reactively updates
 * window.__RUNBOOK_CONFIG__ when the runbook mode is switched at runtime.
 *
 * The parent server component (RunbookConfig) re-renders on router.refresh()
 * and passes the new configJson prop. This triggers our useEffect which:
 * 1. Updates window.__RUNBOOK_CONFIG__
 * 2. Dispatches 'runbook-config-changed' so useRunbook() hooks re-resolve
 *
 * The initial page load is handled by the sibling <script> tag (synchronous,
 * before React hydration). This component only handles subsequent updates.
 */

import { useEffect, useRef } from 'react';

export function RunbookConfigUpdater({ configJson }: { configJson: string }) {
  const initialRef = useRef(true);

  useEffect(() => {
    // Skip first render — the <script> tag already set __RUNBOOK_CONFIG__
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }

    // Subsequent renders (triggered by router.refresh() after runbook switch)
    try {
      window.__RUNBOOK_CONFIG__ = JSON.parse(configJson);
      window.dispatchEvent(new Event('runbook-config-changed'));
    } catch {
      // Malformed JSON — should never happen since server serialized it
    }
  }, [configJson]);

  return null;
}
